// BUILD-046 consolidated pass 2026-10-23 (Assigned by Me): text that Compact cuts short with an ellipsis (a task title, a Customer / Job, "Anyone in Accounting") must still be readable in full by pointer AND by keyboard.
//
// When the text is actually cut (measured, and measured again when the column changes size) it takes a tab stop and shows its full text in the same tooltip the people's names use (PersonName.tsx useNameTip): on hover,
// on keyboard focus, dismissed with Escape, never clipped by the table. When the text fits it is plain text with no tab stop and no tooltip, so a table of 100 rows is not 100 extra tab stops. Screen readers always read the
// full text (the cut is only visual).
import { useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNameTip } from './PersonName';

export interface TruncatedTextProps {
  /** The full text: what the tooltip says. */
  text: string;
  /** What is drawn (defaults to `text`); a host with rich content (a quiet prefix) passes it here. */
  children?: ReactNode;
  className?: string;
  /** Draws as a block (a cell's only content) instead of inline. */
  block?: boolean;
  testId?: string;
}

export function TruncatedText({ text, children, className, block = false, testId }: TruncatedTextProps) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [cut, setCut] = useState(false);
  const tip = useNameTip(cut ? text : null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) return undefined;
    const measure = (): void => setCut(element.scrollWidth > element.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text]);
  return (
    <>
      <span
        ref={(element) => {
          ref.current = element;
          tip.setAnchor(element);
        }}
        tabIndex={cut ? 0 : undefined}
        // The browser's own bubble would show beside the tooltip that is already open: it is withdrawn while the tooltip shows.
        title={cut && !tip.open ? text : undefined}
        aria-describedby={tip.open ? tip.tipId : undefined}
        data-testid={testId}
        data-cut={cut ? 'true' : 'false'}
        className={`${block ? 'block ' : ''}min-w-0 truncate ${cut ? 'rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]' : ''} ${className ?? ''}`.trim()}
        {...tip.handlers}
      >
        {children ?? text}
      </span>
      {tip.overlay}
    </>
  );
}
